/* eslint-disable react/prop-types */
import React, { memo, useImperativeHandle, useRef, forwardRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import { Autocomplete as BaseAutocomplete } from '../Controls'
import { handleRequired, ErrorMessage, useFormBase, formImperativeHandleFunctions, handleFormPostObject } from './common'
import { translate } from '../../multilanguage'
import { FormFieldContainer } from './constants'
import { useTranslation } from 'react-i18next'

/**
 * Implements a MUI Autocomplete for forms.
 *
 * @param {object} props component props
 * @returns {Autocomplete} Autocomplete component
 */
const Autocomplete = memo(forwardRef((props, ref) => {
  const { t } = useTranslation()
  const { fieldTemplate, data, heading, options, label, ...finalProps } = props
  const [selectItems, setSelectItems] = useState(options)
  const originalItems = useRef(options)

  useEffect(() => {
    if (options?.length > 0) {
      originalItems.current = [...options]
    }
    setSelectItems(options)
  }, [options])

  const { onChange, errorMsgFixedHeight, disabled, setDisabled, fieldLabel, refFormElement, valueRef, errorMsg, setErrorMsg } = 
    useFormBase(props, fieldTemplate, data)

  useImperativeHandle(ref, () => {
    const funcsObj = formImperativeHandleFunctions(fieldTemplate, refFormElement, valueRef, setErrorMsg, setDisabled, true)
    return {
      ...funcsObj,
      getValue: () => valueRef.current,
      setValue: value => {
        refFormElement.current.setValue(value)
        valueRef.current = value
      },
      clear: () => {
        funcsObj.clear()
        setSelectItems(originalItems.current)
      },
      setSelectItems: items => setSelectItems(items),
      getItems: () => selectItems,
      validate: (postObject, refs) => {
        const value = valueRef.current
        let error = ''

        if (fieldTemplate.isRequired && value === null) {
          error = handleRequired(props)
        } else if (fieldTemplate.validator) {
          error = translate(fieldTemplate.validator(value))
        }

        return handleFormPostObject(postObject, fieldTemplate, value, refs, error, setErrorMsg)
      },
    }
  })

  return (
    <>
      {heading && <h1 style={{ color: '#1975BC', fontSize: '22px', marginLeft: '5px' }}>{heading}</h1>}
      <FormFieldContainer>
        <BaseAutocomplete
          {...finalProps}
          options={selectItems}
          disabled={finalProps.disabled !== undefined ? finalProps.disabled : disabled}
          ref={refFormElement}
          onChange={onChange}
          error={!!errorMsg}
          label={fieldLabel.current && t(`${fieldLabel.current}`)}
          noTranslate={true}
        />
        <ErrorMessage errorMsg={errorMsg} errorMsgFixedHeight={errorMsgFixedHeight} />
      </FormFieldContainer>
    </>
  )
}))

Autocomplete.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Options Array for the control.
   */
  options: PropTypes.array.isRequired,
  /**
   Used to determine the string value for a given option. It's used to fill the input (and the list box options).
   Signature: function(option: T) => string
   */
  getOptionLabel: PropTypes.func,
  /**
   Used to determine which option in the array was selected.
   Signature: function(option: T, optionSelected: T) => string
   */
  getOptionSelected: PropTypes.func,
  /**
   Given an item in options, this returns the value/code of the item.
   */
  getOptionValue: PropTypes.func, 
  /**
   Label to display for the control.
   */
  label: PropTypes.string,
  /**
   onChange function called when an options is selected.
   */
  onChange: PropTypes.func,
  /**
   Prevents the language translation of the label.
   */
  noTranslate: PropTypes.bool,
  /**
   Displays the control in an error state.
   */
  error: PropTypes.bool,
  /**
   Props applied to the Input element.
   */
  inputProps: PropTypes.object,
  /**
   Props applied to the label.
   */
  InputLabelProps: PropTypes.object,
  /**
   Determines if the control is disabled.
   */
  disabled: PropTypes.bool,
  /**
   Field definition template for the control.
   */
  fieldTemplate: PropTypes.object.isRequired,
  /**
   The data object retrieved from the backend which contains all data for the form controls.
   */
  data: PropTypes.object,
  /**
   Set a ref to the component in your page and call this function to restore its default value contained in the fieldTemplate prop.
   */
  'ref.current.clear': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to get its current value.
   */
  'ref.current.getValue': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to set its current value.
   */
  'ref.current.setValue': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to run the validators in the fieldTemplate prop.
   */
  'ref.current.validate': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to disable the control.
   */
  'ref.current.setDisabled': PropTypes.func,
  /**
   Set the select items.
   */
  'ref.current.setSelectItems': PropTypes.func,
  /**
   Get the select items.
   */
  'ref.current.getItems': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to set the error message for the control.
   */
  'ref.current.setErrorMsg': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to clear the error message for the control.
   */
  'ref.current.clearErrorMsg': PropTypes.func,
  /**
   Returns the form template associated with this form control.
   */
  'ref.current.getFormTemplate': PropTypes.func,
}

export default Autocomplete